import { useContext, useEffect, useState } from "react";
import { Button, ProtectedComponent, Tabs, UserSideBar } from "../../components";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import { AuthPermission } from "../../services/auth.service";
import Plans from "./Plans";
import Subscribers from "./Subscribers";
import Services from "./Services";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { useParams, useSearchParams } from "react-router-dom";
import Notifications from "./Notifications";
import AnnualPlan from "../../components/forms/AnnualPlan";
import Step2 from "../../components/forms/AnnualPlan/Step2";
import { useSelector } from "react-redux";
import { searchAnnualPlanServices } from "../../redux/slices/ServiceSlice";
import { Service } from "../../models/service.model";
import { AnnualPlanServices } from "../../models/annual-plan.model";
import { updateOfficeAnnualPlanServices } from "../../redux/slices/annualPlanSlice";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { getOffice } from "../../redux/slices/OfficeSlice";
import Archive from "./Archive";

const OfficePlan = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("activeTab");
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const { blockFeeServicesSortObj } = useSelector((state: RootState) => state.service);
  const [activeIndex, setActiveIndex] = useState(0);
  const [createPlan, setCreatePlan] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [showEditServices, setShowEditServices] = useState(false);
  const [loading, setLoading] = useState(false);
  const selectedPlans = useSelector((state: RootState) => state.selectedPlans.selectedPlans);

  const changeServiceTab = (index: number) => {
    setActiveIndex(index);
  };

  const handleClose = () => {
    setLoading(false);
    setCreatePlan(false);
  };

  const updateServices = async (val?: boolean) => {
    if (!val && id) {
      setLoading(true);
      const body: AnnualPlanServices[] = services
        .filter(item => item?.annualPlanDiscountPct && item?.annualPlanDiscountPct > 0)
        .map(item => ({
          serviceId: item.serviceId ?? "",
          annualPlanEnabled: item.annualPlanEnabled ?? false,
          annualPlanDiscountPct: item.annualPlanDiscountPct ?? 0,
        }));
      await dispatch(
        updateOfficeAnnualPlanServices({
          officeId: id,
          body: body,
        })
      );
      await dispatch(
        searchAnnualPlanServices({
          page: blockFeeServicesSortObj.pageNumber,
          size: blockFeeServicesSortObj.rowsPerPage,
          sort: blockFeeServicesSortObj.sortedBy,
          term: blockFeeServicesSortObj.searchTerm,
          officeId: id,
        })
      );
      toast?.openToast("Services updated successfully!", 2000, ToastVariants.Success);
      setLoading(false);
    }
    setShowEditServices(false);
  };

  useEffect(() => {
    if (currentOffice === null && id) {
      dispatch(getOffice({ officeId: id }));
    }
  }, [currentOffice]);

  useEffect(() => {
    if (activeTab === "subscribers") {
      changeServiceTab(1);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("activeTab"); // Remove the activeTab parameter
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  if (!currentOffice) return null;

  return (
    <div className="container mx-auto p-8">
      <div className="relative">
        <OfficeSectionsHeader showMeta={true} />
        {activeIndex === 0 ? (
          <div className="sm:absolute top-0 bottom-0 right-10 flex items-center">
            <ProtectedComponent requiredPermission={[AuthPermission.Service]}>
              <div className="block md:flex md:justify-end items-center ml-0 md:ml-5">
                <Button
                  text={
                    currentOffice?.annualPlanActive && selectedPlans?.length && !selectedPlans.some(plan => !plan.isActive && !plan.checked)
                      ? "Edit Annual Plan"
                      : "Activate Annual Plan"
                  }
                  onClick={() => setCreatePlan(true)}
                  disabled={selectedPlans?.length ? false : true}
                  className={`whitespace-nowrap w-auto mt-3 md:mt-0`}
                />
              </div>
            </ProtectedComponent>
          </div>
        ) : null}
        {activeIndex === 2 && currentOffice?.annualPlanActive ? (
          <div className="sm:absolute top-0 bottom-0 right-10 flex items-center">
            <ProtectedComponent requiredPermission={[AuthPermission.Service]}>
              <div className="block md:flex md:justify-end items-center ml-0 md:ml-5">
                <Button text="Edit Services" onClick={() => setShowEditServices(true)} className="whitespace-nowrap w-auto mt-3 md:mt-0" />
              </div>
            </ProtectedComponent>
          </div>
        ) : null}
      </div>
      <div className="block lg:flex justify-between items-center">
        <div className="w-full lg:w-1/2 mb-5 lg:mb-0">
          <h2 className="font-extrabold text-2xl uppercase">Annual Plan</h2>
          {!currentOffice?.annualPlanActive ? (
            <p className="text-base font-medium">You are currently not offering an annual plan to your patients.</p>
          ) : null}
        </div>
      </div>
      <div>
        <div className="grid grid-cols-1 gap-4 mb-2">
          <Tabs
            components={[
              <Plans key={"plans"} />,
              <Subscribers key={"subscribers"} />,
              <Services key={"servces"} officeId={id ?? ""} />,
              <Notifications key={"notifications"} />,
              <Archive key={"archive"} />,
            ]}
            options={["Plans", "Subscribers", "Services", "Notifications", "Archive"]}
            activeTab={activeIndex}
            onClickTab={changeServiceTab}
            fromOffice={false}
            showBottomBorder={false}
          />
        </div>
      </div>
      {createPlan && id ? (
        <UserSideBar title={""} visible={true} onClickOutside={() => null} onCancelClick={handleClose} onInviteClick={handleClose}>
          <AnnualPlan officeId={id} handleClose={handleClose} />
        </UserSideBar>
      ) : null}
      {showEditServices && id ? (
        <UserSideBar
          title={""}
          visible={true}
          onClickOutside={() => (!loading ? setShowEditServices(false) : null)}
          onCancelClick={() => setShowEditServices(false)}
          onInviteClick={() => setShowEditServices(false)}
        >
          <div className="px-5">
            <Step2 officeId={id} isEditServices={true} services={services} setServices={setServices} changeStep={val => updateServices(val)} />
          </div>
        </UserSideBar>
      ) : null}
    </div>
  );
};

export default OfficePlan;
